import { useEffect, useState } from 'react';
import { Alert, Box, Divider, Heading, List, Paragraph, Text, Toast, useToast } from '@zillow/constellation-10';
import { useRouter } from 'next/router';
import { useFormContext } from 'react-hook-form';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useCappsPaymentWebApiHandler_startPaymentMethodSession,
  useCappsPaymentWebApiHandler_submitApplicationPayment,
} from '@zg-rentals/ts-capps-client-react-query';
import type { StartPaymentMethodSessionWebApiResponseDto } from '@zg-rentals/ts-capps-client';
import { ApplePayButton, PaymentMethodIframe } from '@zillow/metroid-components';
import type { SessionToken } from '@zillow/metroid-payment-method';
import type {
  SessionToken as MetroidComponentSessionToken,
  MetroidResponse,
  // @ts-expect-error - metroid-components types not exported for bundler resolution
} from '@zillow/metroid-components/dist/types/lib/types';
import { logError } from '@zg-rentals/log-error';
import { getCurrencyDisplayFromDollars } from '@zg-rentals/formatters';
import { LoadingText } from '@zg-rentals/loading-components';
import { useAuth } from '../AuthProvider';
import { useIsApplePayEnabled } from '../../utils/ab-tests';
import { TallHeightLoadingMask } from '../../styles/css-components';
import { useMetroidConfig } from '../../hooks/useMetroidConfig';
import { REQUESTING_CLIENT } from '../../constants/api';
import { getEditRoute } from '../../constants/urls';
import { FIELD_IDS, STEP_IDS } from '../../constants/wizard';
import { invalidateRentalApplication } from '../../hooks/useRentalApplication';
import { formatDate } from '../../utils/formatters';
import { useApplicationFee } from '../../hooks/useApplicationFee';
import { useIsInMobileWebView } from '../../hooks/useIsInMobileWebView';
import {
  ACTIONABLE_METROID_ERROR_CODES,
  getMetroidErrorMessage,
  METROID_ERROR_MESSAGES,
  METROID_ERROR_REGEX,
} from '../../constants/metroid';
import {
  emitApplePayButtonVisibleEvent,
  emitApplePayPurchaseReportsEvent,
  emitApplePaySuccessfulEvent,
  emitNonApplePaySuccessfulEvent,
  emitPaymentFormLoadingCompleteEvent,
  emitPaymentFormStartsLoadingEvent,
  emitPurchaseReportsEvent,
} from '../../analytics/clickstreamEvents';
import { useApplicationContextOrUndefined } from '../../contexts/ApplicationContext';

export function PaymentForm({
  applicationId,
  onSuccess,
  onError,
  topUp = false,
  expirationDate,
}: {
  applicationId: string;
  onSuccess?: () => void;
  onError?: () => void;
  topUp?: boolean;
  expirationDate?: Date | null;
}) {
  const router = useRouter();
  const { enqueueToast } = useToast();
  const { getValues } = useFormContext();
  const { user: userData } = useAuth();
  const firstName = getValues('firstName');
  const lastName = getValues('lastName');
  const phone = getValues('phone');
  const street = getValues(FIELD_IDS.CURRENT_RESIDENCE_STREET);
  const city = getValues(FIELD_IDS.CURRENT_RESIDENCE_CITY);
  const zip = getValues(FIELD_IDS.CURRENT_RESIDENCE_ZIP);
  const state = getValues(FIELD_IDS.CURRENT_RESIDENCE_STATE);

  const hasAllRequiredData = !!(firstName && lastName && phone && street && city && state && zip);

  const needsName = !firstName || !lastName;
  const needsPhone = !phone;
  const needsAddress = !street || !city || !zip || !state;

  useEffect(() => {
    if (needsName) {
      enqueueToast(
        <Toast tone="info">You need to enter your first and last name in order to process your payment</Toast>,
      );
      router.push(
        getEditRoute({
          applicationId,
          stepId: STEP_IDS.LEGAL_NAME,
          nextStepId: STEP_IDS.PAYMENT_AND_REPORTS,
        }),
      );
    } else if (needsPhone) {
      enqueueToast(<Toast tone="info">You need to enter your phone number in order to process your payment</Toast>);
      router.push(
        getEditRoute({
          applicationId,
          stepId: STEP_IDS.CONTACT,
          nextStepId: STEP_IDS.PAYMENT_AND_REPORTS,
        }),
      );
    } else if (needsAddress) {
      enqueueToast(<Toast tone="info">You need to enter your address in order to process your payment</Toast>);
      router.push(
        getEditRoute({
          applicationId,
          stepId: STEP_IDS.CURRENT_RESIDENCE,
          nextStepId: STEP_IDS.PAYMENT_AND_REPORTS,
        }),
      );
    }
  }, [needsName, needsPhone, needsAddress]);

  const userId = userData?.userToken as string;

  return hasAllRequiredData && userId ? (
    <PaymentMetroidForm
      applicationId={applicationId}
      userId={userId}
      onSuccess={onSuccess}
      onError={onError}
      topUp={topUp}
      expirationDate={expirationDate}
    />
  ) : null;
}

type PaymentMethodSourceType = 'credit-card' | 'applePay';

type ApplePayLineItem = { label: string; amount: string; type?: 'final' | 'pending' };

const APPLE_PAY_LABEL = 'Zillow Rentals';

type PaymentCopy = {
  feeDisplay?: string;
  taxDisplay?: string;
  totalDisplay?: string;
  trueUpDisplay?: string;
  topUp: boolean;
  isLoading: boolean;
};

function ZillowPaymentCopy({ feeDisplay, taxDisplay, topUp, isLoading }: PaymentCopy) {
  return (
    <Paragraph>
      <LoadingText isLoading={isLoading}>
        {`Your credit or debit card will be charged ${topUp ? 'an additional' : 'a'} ${feeDisplay ?? ''} one-time fee${taxDisplay ? ` plus ${taxDisplay} in sales tax` : ''}. This covers application submissions for 30 days, plus credit and background checks from our partners, Experian and CIC (Contemporary Information Corporation).`}
      </LoadingText>
    </Paragraph>
  );
}

function StreetEasyPaymentCopy({ taxDisplay, totalDisplay, trueUpDisplay, isLoading }: PaymentCopy) {
  return (
    <>
      <Paragraph>
        <LoadingText isLoading={isLoading}>
          {`Your credit or debit card will be charged ${totalDisplay ?? ''}${taxDisplay ? ', including tax' : ''}. This one-time payment includes:`}
        </LoadingText>
      </Paragraph>
      <List.Root>
        <List.Item>A Zillow application, reusable for 30 days on eligible StreetEasy listings</List.Item>
        <List.Item>An Experian credit report</List.Item>
        <List.Item>A CIC background check</List.Item>
      </List.Root>
      <Paragraph css={{ marginTop: 'default' }}>
        <LoadingText isLoading={isLoading}>
          {`Later, if you want to reuse your application on eligible Zillow listings, you can do so for a one-time ${trueUpDisplay ?? ''} payment, plus tax.`}
        </LoadingText>
      </Paragraph>
    </>
  );
}

function PaymentMetroidForm({
  applicationId,
  userId,
  onSuccess,
  onError,
  topUp,
  expirationDate,
}: {
  applicationId: string;
  userId: string;
  onSuccess?: () => void;
  onError?: () => void;
  topUp: boolean;
  expirationDate?: Date | null;
}) {
  const { enqueueToast } = useToast();
  const { metroidConfigUrl, metroidHPPConfigId } = useMetroidConfig();

  const [sessionToken, setSessionToken] = useState<MetroidComponentSessionToken>();
  const [metroidAuthToken, setMetroidAuthToken] = useState<string>();
  const [isLoadingIFrame, setIsLoadingIFrame] = useState(true);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const queryClient = useQueryClient();
  const applicationContext = useApplicationContextOrUndefined();

  const translateSessionToken = (token: SessionToken): MetroidComponentSessionToken => {
    return {
      reference_key: token.referenceKey,
      secret: token.secret,
    };
  };

  const { mutate: startPaymentMethodMutation, isPending } = useCappsPaymentWebApiHandler_startPaymentMethodSession({
    onMutate: () => {
      emitPaymentFormStartsLoadingEvent(applicationContext);
    },
    onSuccess: async (response: StartPaymentMethodSessionWebApiResponseDto) => {
      setSessionToken(translateSessionToken(response.sessionToken));
      setMetroidAuthToken(response.metroidAuthToken);
    },
    onError: (error) => {
      logError({ error, context: 'PaymentMetroidForm - startPaymentMethodMutation error' });
      enqueueToast(<Toast tone="critical">We couldn’t load the payment form. Refresh the page and try again.</Toast>);
    },
  });

  const submitPaymentMutation = useCappsPaymentWebApiHandler_submitApplicationPayment({
    meta: {
      invalidates: ['applicationPaymentRequirement'],
    },
  });

  const { getValues } = useFormContext();
  const firstName = getValues(FIELD_IDS.FIRST_NAME);
  const lastName = getValues(FIELD_IDS.LAST_NAME);
  const email = getValues(FIELD_IDS.EMAIL);
  const phone = getValues(FIELD_IDS.PHONE);
  const street = getValues(FIELD_IDS.CURRENT_RESIDENCE_STREET);
  const city = getValues(FIELD_IDS.CURRENT_RESIDENCE_CITY);
  const zip = getValues(FIELD_IDS.CURRENT_RESIDENCE_ZIP);
  const state = getValues(FIELD_IDS.CURRENT_RESIDENCE_STATE);

  const isApplePayEnabled = useIsApplePayEnabled();

  const { previewPaymentData, feeDisplay, isLoading: isFeeLoading } = useApplicationFee({ applicationId });

  const requiredFeeDisplay = previewPaymentData
    ? getCurrencyDisplayFromDollars({ amount: (previewPaymentData.requiredPriceInCents / 100).toFixed(2) })
    : undefined;
  const coveredFeeDisplay = previewPaymentData
    ? getCurrencyDisplayFromDollars({ amount: (previewPaymentData.coveredPriceInCents / 100).toFixed(2) })
    : undefined;
  const deltaFeeDisplay = previewPaymentData
    ? getCurrencyDisplayFromDollars({ amount: (previewPaymentData.deltaInCents / 100).toFixed(2) })
    : undefined;
  const totalDisplay = previewPaymentData
    ? getCurrencyDisplayFromDollars({ amount: previewPaymentData.invoiceAmount })
    : undefined;
  const trueUpDisplay = previewPaymentData
    ? getCurrencyDisplayFromDollars({
        amount: ((previewPaymentData.defaultPriceInCents - previewPaymentData.requiredPriceInCents) / 100).toFixed(2),
      })
    : undefined;
  const taxDisplay =
    previewPaymentData && Number(previewPaymentData.taxAmount) > 0
      ? getCurrencyDisplayFromDollars({ amount: previewPaymentData.taxAmount })
      : undefined;
  const windowEndsDisplay = formatDate(expirationDate);

  const applePayAmount = previewPaymentData ? previewPaymentData.invoiceAmount : undefined;

  const applePayLineItems: Array<ApplePayLineItem> | undefined =
    previewPaymentData && Number(previewPaymentData.taxAmount) > 0
      ? [
          {
            label: APPLE_PAY_LABEL,
            amount: previewPaymentData.amountWithoutTax,
            type: 'final',
          },
          { label: 'Sales tax', amount: previewPaymentData.taxAmount, type: 'final' },
        ]
      : undefined;

  function initializePaymentSession() {
    setSessionToken(undefined);
    setMetroidAuthToken(undefined);
    setIsLoadingIFrame(true);
    startPaymentMethodMutation({
      postData: {
        address1: street,
        city,
        country: 'United States',
        lastName,
        firstName,
        workEmail: email,
        homePhone: phone,
        postalCode: zip,
        paymentType: 'applications',
        stateOrProvince: state,
      },
    });
  }

  useEffect(() => {
    if (retryCount <= 2) {
      initializePaymentSession();
    }
  }, [retryCount]);

  function onResetPaymentSession(error: object) {
    onError?.();
    logError({ error, context: 'PaymentMetroidForm - onResetPaymentSession error' });
    initializePaymentSession();
  }

  async function onSubmitForm(paymentMethodSourceType: PaymentMethodSourceType): Promise<boolean> {
    if (!isSubmittingPayment) {
      emitPurchaseReportsEvent(applicationContext);
      if (paymentMethodSourceType === 'applePay') {
        // Ideally we should track this event on the "onpaymentmethodselected"
        // event but it's not exposed by metroid-components, so we track it here
        // a little bit later, this means that it will not be tracked (incorrect)
        // when the user selects a payment method but it is not authorized
        emitApplePayPurchaseReportsEvent(applicationContext);
      }
      setIsSubmittingPayment(true);
      let isPaymentSuccessful = false;
      try {
        const response = await submitPaymentMutation.mutateAsync({
          postData: {
            applicationId,
            requestingClient: REQUESTING_CLIENT,
            paymentMethodSourceType,
          },
        });
        if (response.isChargeSuccess || response.responseType === 'VALID_PAYMENT_EXISTS') {
          if (paymentMethodSourceType === 'applePay') {
            // Putting the trackEvent here, outside the isChargeSuccess if statement
            // because the if doesn't affect the isPaymentSuccessful variable
            emitApplePaySuccessfulEvent(applicationContext);
          } else {
            emitNonApplePaySuccessfulEvent(applicationContext);
          }

          if (response.isChargeSuccess) {
            enqueueToast(<Toast tone="success">Payment received. We’ll email you a confirmation.</Toast>);
          }
          onSuccess?.();
          isPaymentSuccessful = true;
        } else if (response.isChargeDeclined) {
          enqueueToast(<Toast tone="critical">The payment was declined. Try again.</Toast>);
          onResetPaymentSession(response);
        } else {
          enqueueToast(<Toast tone="critical">The payment was not successful. Try again.</Toast>);
          onResetPaymentSession(response);
        }
      } catch (e) {
        enqueueToast(<Toast tone="critical">The payment was not successful. Try again.</Toast>);
        onResetPaymentSession(e);
      }

      if (isPaymentSuccessful) {
        await invalidateRentalApplication(queryClient);
      }

      setIsSubmittingPayment(false);
      return isPaymentSuccessful;
    }
    return false;
  }

  async function onSubmitFailure(e: MetroidResponse<null>) {
    const { code, code_details } = e;
    let isClientError = false;
    if (
      code === ACTIONABLE_METROID_ERROR_CODES.EXPIRED_SESSION_TOKEN ||
      code === ACTIONABLE_METROID_ERROR_CODES.INVALID_SESSION_TOKEN
    ) {
      setRetryCount((currentRetryCount) => currentRetryCount + 1);
    } else if (code === ACTIONABLE_METROID_ERROR_CODES.ZEUI) {
      // ZE-UI errors are usually caused by the user
      if (code_details && METROID_ERROR_REGEX.DOWNSTREAM.test(code_details)) {
        // this specific error has nothing to do with user input, so only allow one retry in case it was a blip
        setRetryCount((currentRetryCount) => currentRetryCount + 1);
      } else {
        isClientError = true;
        enqueueToast(<Toast tone="critical">{getMetroidErrorMessage(e)}</Toast>);
        onResetPaymentSession(e);
      }
    } else {
      enqueueToast(<Toast tone="critical">{METROID_ERROR_MESSAGES.GENERIC}</Toast>);
      onResetPaymentSession(e);
    }
    logError({
      error: e,
      context: { location: 'PaymentMetroidForm', action: 'onSubmitFailure', isClientError, code, code_details },
      errorType: `SubmitPayment${isClientError ? 'ClientError' : 'ServerError'}`,
    });
  }

  const PaymentCopyComponent = previewPaymentData?.isStreetEasy ? StreetEasyPaymentCopy : ZillowPaymentCopy;

  const isInWebView = useIsInMobileWebView();
  const canApplePayBeShown =
    isApplePayEnabled && sessionToken && metroidAuthToken && !isInWebView && window.ApplePaySession;
  useEffect(() => {
    if (canApplePayBeShown) {
      emitApplePayButtonVisibleEvent(applicationContext);
    }
  }, [canApplePayBeShown]);

  return (
    <TallHeightLoadingMask loading={isPending || isLoadingIFrame || isSubmittingPayment}>
      {sessionToken && metroidAuthToken && (
        <Box
          css={{
            '& #z_hppm_iframe': {
              backgroundColor: '!transparent',
              width: '100%',
            },
          }}
        >
          {topUp && (
            <Alert.Root tone="info" css={{ marginBottom: 'default' }}>
              <Alert.Body>
                <Paragraph>
                  <LoadingText isLoading={isFeeLoading}>
                    This home isn’t a StreetEasy exclusive. A {deltaFeeDisplay} fee difference applies.
                  </LoadingText>
                </Paragraph>
                <Paragraph css={{ marginTop: 'default' }}>
                  This doesn’t extend your 30-day window, which still ends {windowEndsDisplay}.
                </Paragraph>
              </Alert.Body>
            </Alert.Root>
          )}
          <Box css={{ marginBottom: 'default' }}>
            <Heading level={3} textStyle="heading-xs" css={{ marginBottom: 'xs' }}>
              Provide your payment details
            </Heading>
            <PaymentCopyComponent
              feeDisplay={feeDisplay}
              taxDisplay={taxDisplay}
              totalDisplay={totalDisplay}
              trueUpDisplay={trueUpDisplay}
              topUp={topUp}
              isLoading={isFeeLoading}
            />
          </Box>
          <PaymentMethodIframe
            onSubmitSuccess={(_) => onSubmitForm('credit-card')}
            onSubmitFailure={onSubmitFailure}
            onRenderComplete={() => {
              emitPaymentFormLoadingCompleteEvent(applicationContext);
              setIsLoadingIFrame(false);
            }}
            sessionToken={sessionToken}
            metroidAccountId={{
              userId,
              brand: 'Zillow',
              businessUnit: 'Rentals Consumers',
            }}
            metroidEndpointUrl={metroidConfigUrl}
            metroidHPPConfigId={metroidHPPConfigId}
            setAsDefault
          />
          {canApplePayBeShown && applePayAmount && (
            <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default', margin: 'tightest' }}>
              <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(13, 1fr)', alignItems: 'center' }}>
                <Box css={{ gridColumnEnd: 'span 6' }}>
                  <Divider orientation="horizontal" role="presentation" />
                </Box>
                <Box css={{ gridColumnEnd: 'span 1' }}>
                  <center>
                    <Text>Or</Text>
                  </center>
                </Box>
                <Box css={{ gridColumnEnd: 'span 6' }}>
                  <Divider orientation="horizontal" role="presentation" />
                </Box>
              </Box>
              <Box
                css={{ '& apple-pay-button': { width: '100%' } }}
                style={{ '--apple-pay-button-height': '44px' } as React.CSSProperties}
              >
                <ApplePayButton
                  key={`${applePayAmount}-${applePayLineItems?.map((li) => li.amount).join(',') ?? ''}`}
                  brand="Zillow"
                  businessUnit="Rentals Consumers"
                  data={{
                    amount: applePayAmount,
                    label: APPLE_PAY_LABEL,
                    type: 'final',
                  }}
                  lineItems={applePayLineItems}
                  metroidAuthToken={metroidAuthToken}
                  metroidEndpointUrl={metroidConfigUrl}
                  processTransaction={(_) => onSubmitForm('applePay')}
                  onComplete={function ra() {}}
                  onError={onSubmitFailure}
                  type="pay"
                  setAsDefault
                />
              </Box>
            </Box>
          )}
        </Box>
      )}
    </TallHeightLoadingMask>
  );
}
